import * as React from "react";

import { cn } from "../lib/utils";

/**
 * Detail-Layout-Familie (TES-1999) — die visuelle Sprache der Detail-Ansichten
 * (Stitch screen-07 / DefectDetailContent) als wiederverwendbare Primitives, damit
 * View- UND Edit-Flächen dieselbe Struktur sprechen (TES-1998).
 *
 * REINE UI-HUELLEN — keine Datenanbindung, i18n-Strings kommen vom Aufrufer.
 *
 * - `DetailGrid`      12-col responsive Detail-Raster (mobile gestapelt)
 * - `DetailGridMain`  linke Hauptspalte (col-span-8)
 * - `DetailGridAside` rechte Randspalte (col-span-4)
 * - `DetailSection`   Sektions-Card mit Icon+Titel-Header (padded | divided | muted)
 */

type DivProps = React.HTMLAttributes<HTMLDivElement>;

const DetailGrid = React.forwardRef<HTMLDivElement, DivProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("grid grid-cols-1 lg:grid-cols-12 gap-4", className)} {...props} />
));
DetailGrid.displayName = "DetailGrid";

const DetailGridMain = React.forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("lg:col-span-8 space-y-4 min-w-0", className)} {...props} />
  ),
);
DetailGridMain.displayName = "DetailGridMain";

const DetailGridAside = React.forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("lg:col-span-4 space-y-4 min-w-0", className)} {...props} />
  ),
);
DetailGridAside.displayName = "DetailGridAside";

export interface DetailSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Header-Icon-Slot (Aufrufer setzt Groesse/Farbe, z. B. `<Link2 className="h-5 w-5 text-primary" />`). */
  icon?: React.ReactNode;
  /** Sektions-Titel (fertiger i18n-String oder Node). Ohne Titel wird kein Header gerendert. */
  title?: React.ReactNode;
  /** Rechter Header-Slot (Badge, Zaehler, Aktion). */
  headerRight?: React.ReactNode;
  /**
   * - `padded` (Default): p-6-Card, Header mit mb-4
   * - `divided`: p-4-Header mit Trennlinie, Body ungepolstert (Tabellen/Listen)
   */
  layout?: "padded" | "divided";
  /** `card` (Default) weisse Card · `muted` surface-container-low (z. B. Activity-Log). */
  tone?: "card" | "muted";
}

/** True, wenn die Sektion eine Header-Zeile rendert (Icon-, Titel- oder Rechts-Slot gesetzt). */
function hasHeaderSlots(
  icon: React.ReactNode,
  title: React.ReactNode,
  headerRight: React.ReactNode,
): boolean {
  return icon !== undefined || title !== undefined || headerRight !== undefined;
}

function DetailSectionHeader({
  icon,
  title,
  headerRight,
  layout,
}: Pick<DetailSectionProps, "icon" | "title" | "headerRight"> & {
  layout: "padded" | "divided";
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3",
        layout === "divided" ? "p-4 border-b border-outline-variant" : "mb-4",
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        {title !== undefined && (
          <h3 className="font-display text-lg font-semibold break-words">{title}</h3>
        )}
      </div>
      {headerRight}
    </div>
  );
}

const DetailSection = React.forwardRef<HTMLElement, DetailSectionProps>(
  (
    { className, icon, title, headerRight, layout = "padded", tone = "card", children, ...props },
    ref,
  ) => (
    <section
      ref={ref}
      className={cn(
        "border border-outline-variant rounded-lg",
        tone === "muted" ? "bg-surface-container-low" : "bg-card",
        layout === "padded" && "p-6",
        className,
      )}
      {...props}
    >
      {hasHeaderSlots(icon, title, headerRight) && (
        <DetailSectionHeader icon={icon} title={title} headerRight={headerRight} layout={layout} />
      )}
      {children}
    </section>
  ),
);
DetailSection.displayName = "DetailSection";

export { DetailGrid, DetailGridMain, DetailGridAside, DetailSection };
